import React, { useState } from 'react';
import classNames from 'classnames';

import { filterByInclusion } from 'shared';

import Button from '../Button';
import { DS_CLASS_NAME, SCROLLABLE_BOX_SHADOW } from '../constants';
import IconButton from '../IconButton';
import Layout from '../Layout';
import Text from '../Text';
import Typeahead, { Result } from '../Typeahead';

import { HEADER_INPUT_SIZE } from './constants';
import { FilterOption, SetFilter } from './types';

interface Props {
  options: FilterOption[];
  oneOfValues: (string | number | boolean)[];
  setFilter: SetFilter;
}

const TableSearchableOneOfFilter: React.FC<Props> = ({ options, oneOfValues, setFilter }) => {
  const [query, setQuery] = useState('');

  const queryTrimmed = query.trim();
  const typeaheadResults: Result<string>[] = options
    .filter(({ value }) => !oneOfValues.includes(value))
    .filter(({ label }) => (queryTrimmed.length > 0 ? filterByInclusion(label, queryTrimmed) : true))
    .map(({ label, value }) => ({ label, value: String(value) }));
  const hasQuery = queryTrimmed.length > 0;
  const matchLabel =
    typeaheadResults.length === 1 ? `${typeaheadResults.length} match` : `${typeaheadResults.length} matches`;

  return (
    <Layout
      __className={classNames('TableSearchableOneOfFilter', DS_CLASS_NAME)}
      direction="column"
      gap={16}
      width={400}
      color="white"
      borderRadius={16}
      borderWidth={1}
      borderColor="gray-50"
      padding={16}
      boxShadow={SCROLLABLE_BOX_SHADOW}
    >
      <Layout direction="column" gap={8}>
        <Text size="headline-xs" color="gray-900">
          Filter by tag
        </Text>
        {options.length === 0 ? (
          <Text size="body-sm" color="gray-600">
            No tags are available yet.
          </Text>
        ) : (
          <Layout minHeight={40}>
            <Text size="body-sm" color="gray-600">
              {hasQuery ? matchLabel : 'Search below and add tags. Rows match if they have any of the tags you select.'}
            </Text>
          </Layout>
        )}
      </Layout>

      {oneOfValues.length > 0 ? (
        <Layout direction="column" gap={8} borderTop borderColor="gray-50" paddingTop={16}>
          <Text size="body-sm" color="gray-900" semibold>
            Selected ({oneOfValues.length})
          </Text>
          <Layout direction="row" wrap gap={8} maxHeight={112} overflowY="auto" paddingBottom={4}>
            {oneOfValues.map(tag => (
              <Layout
                key={String(tag)}
                direction="row"
                align="center"
                gap={4}
                color="gray-25"
                borderRadius={8}
                paddingY={6}
                paddingX={8}
              >
                <Text size="body-xs" color="gray-900">
                  {String(tag)}
                </Text>
                <IconButton
                  name="close"
                  size="xs"
                  type="gray"
                  onClick={e => {
                    e.stopPropagation();
                    setFilter({ oneOf: oneOfValues.filter(v => v !== tag) });
                  }}
                />
              </Layout>
            ))}
          </Layout>
        </Layout>
      ) : null}

      {options.length > 0 ? (
        <Layout direction="column" gap={8} borderTop borderColor="gray-50" paddingTop={16}>
          <Text size="body-sm" color="gray-900" semibold>
            Add tags
          </Text>
          <Typeahead
            hideLabel
            label="Search tags"
            placeholder="Search tags…"
            autoFocus
            query={query}
            repositionKey={oneOfValues.length}
            results={typeaheadResults}
            onChange={({ value }) => {
              setQuery(value);
            }}
            onClear={() => {
              setQuery('');
            }}
            onSelect={result => {
              setFilter({ oneOf: [...oneOfValues, result.value] });
              setQuery('');
            }}
            showResultsWhenEmpty
            closeResultsOnSelect={false}
            maxItems={28}
            highlightMatch
            size={HEADER_INPUT_SIZE}
            icon="search"
          />
        </Layout>
      ) : null}

      {options.length > 0 ? (
        <Layout borderTop borderColor="gray-50" paddingTop={16}>
          <Button
            type="secondaryOutline"
            size="sm"
            text="Clear all tags"
            fullWidth
            onClick={() => {
              setFilter({ oneOf: [] });
            }}
          />
        </Layout>
      ) : null}
    </Layout>
  );
};

export default TableSearchableOneOfFilter;
